import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAppointments, createAppointment, cancelAppointment, updateAppointmentStatus, getDoctors } from '../utils/api';

const NAVY = '#0d2137';
const GREEN = '#1a6b3c';
const RED = '#c41e3a';

const emptyForm = { patient_name: '', phone: '', doctor_id: '', appointment_date: '', appointment_time: '', notes: '' };

export default function Appointments() {
  const today = new Date().toISOString().split('T')[0];
  const currentMonth = today.slice(0, 7);
  const [tab, setTab] = useState('daily');
  const [date, setDate] = useState(today);
  const [month, setMonth] = useState(currentMonth);
  const [appointments, setAppointments] = useState([]);
  const [monthlyAppts, setMonthlyAppts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm, appointment_date: today });
  const [saving, setSaving] = useState(false);

  const loadAppts = (d) => {
    setLoading(true);
    getAppointments({ date: d })
      .then(r => setAppointments(r.data))
      .catch(() => toast.error('Failed to load appointments'))
      .finally(() => setLoading(false));
  };

  const loadMonthly = (m) => {
    setLoading(true);
    getAppointments({ month: m })
      .then(r => setMonthlyAppts(r.data))
      .catch(() => toast.error('Failed to load monthly appointments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getDoctors().then(r => setDoctors(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (tab === 'daily') loadAppts(date); }, [date, tab]);
  useEffect(() => { if (tab === 'monthly') loadMonthly(month); }, [month, tab]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handlePhone = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
    let val = digits;
    if (digits.length > 4) val = digits.slice(0, 4) + '-' + digits.slice(4);
    setForm(f => ({ ...f, phone: val }));
  };

  const handleBook = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim()) return toast.error('Patient name is required');
    if (!form.phone.trim()) return toast.error('Phone number is required');
    if (!form.appointment_date) return toast.error('Please select a date');
    if (!form.appointment_time) return toast.error('Please select a time');
    setSaving(true);
    try {
      await createAppointment({ ...form, patient_phone: form.phone, doctor_id: form.doctor_id || null });
      toast.success('Appointment booked');
      setShowForm(false);
      setForm({ ...emptyForm, appointment_date: today });
      if (tab === 'daily' && form.appointment_date === date) loadAppts(date);
      if (tab === 'monthly') loadMonthly(month);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Booking failed');
    }
    setSaving(false);
  };

  const handleStatusChange = async (appt, newStatus) => {
    if (newStatus === appt.status) return;
    if (newStatus === 'cancelled' && !window.confirm(`Cancel appointment for ${appt.patient_name}?`)) return;
    try {
      await updateAppointmentStatus(appt.id, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      if (tab === 'daily') loadAppts(date);
      else loadMonthly(month);
    } catch {
      toast.error('Status update failed');
    }
  };

  const STATUS_COLORS = { scheduled: '#3b82f6', completed: GREEN, cancelled: RED };

  const list = tab === 'daily' ? appointments : monthlyAppts;
  const scheduled = list.filter(a => (a.status || 'scheduled') === 'scheduled').length;
  const completed = list.filter(a => a.status === 'completed').length;
  const cancelled = list.filter(a => a.status === 'cancelled').length;

  const AppTable = ({ data }) => (
    data.length === 0
      ? <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 15 }}>No appointments found.</div>
      : <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: NAVY, color: '#fff' }}>
                {(tab === 'monthly' ? ['#', 'Date', 'Patient', 'Phone', 'Doctor', 'Time', 'Notes', 'Status', 'Action'] : ['#', 'Patient', 'Phone', 'Doctor', 'Time', 'Notes', 'Status', 'Action']).map(h => (
                  <th key={h} style={{ padding: '11px 12px', fontSize: 12, fontWeight: 700, textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((a, i) => (
                <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                  {tab === 'monthly' && (
                    <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: NAVY }}>
                      {new Date(a.appointment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </td>
                  )}
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: NAVY }}>{a.patient_name}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12 }}>{a.patient_phone || '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: GREEN, fontWeight: 600 }}>{a.doctor_name || '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600 }}>{a.appointment_time ? a.appointment_time.slice(0, 5) : '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{a.notes || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${STATUS_COLORS[a.status] || '#94a3b8'}22`, color: STATUS_COLORS[a.status] || '#94a3b8' }}>
                      {a.status || 'scheduled'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <select
                      value={a.status || 'scheduled'}
                      onChange={e => handleStatusChange(a, e.target.value)}
                      style={{ padding: '4px 10px', border: `1.5px solid ${STATUS_COLORS[a.status] || '#94a3b8'}`, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: STATUS_COLORS[a.status] || '#94a3b8', background: '#fff', outline: 'none' }}>
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
  );

  return (
    <div style={{ padding: '32px 28px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ color: NAVY, fontSize: 26, fontWeight: 900 }}>Appointments</h1>
        <button
          onClick={() => setShowForm(s => !s)}
          style={{ padding: '10px 20px', background: GREEN, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
          + Book Appointment
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['daily', 'Daily View'], ['monthly', 'Monthly View']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: '9px 22px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
              background: tab === key ? NAVY : '#f1f5f9', color: tab === key ? '#fff' : '#374151' }}>
            {label}
          </button>
        ))}
      </div>

      {/* Date / Month picker + Summary */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {tab === 'daily'
            ? <input type="date" value={date} onChange={e => setDate(e.target.value)}
                style={{ padding: '9px 14px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, outline: 'none', color: NAVY, fontWeight: 600 }} />
            : <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                style={{ padding: '9px 14px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, outline: 'none', color: NAVY, fontWeight: 600 }} />
          }
        </div>
        {/* Status summary */}
        <div style={{ display: 'flex', gap: 12 }}>
          {[['Scheduled', scheduled, '#3b82f6'], ['Completed', completed, GREEN], ['Cancelled', cancelled, RED]].map(([label, count, color]) => (
            <div key={label} style={{ background: '#fff', borderRadius: 8, padding: '8px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}`, minWidth: 90 }}>
              <div style={{ fontSize: 20, fontWeight: 900, color }}>{count}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Booking form */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px', marginBottom: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', border: `2px solid ${GREEN}` }}>
          <h3 style={{ color: NAVY, marginBottom: 20, fontSize: 16, fontWeight: 800 }}>Book New Appointment</h3>
          <form onSubmit={handleBook}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Patient Name *</label>
                <input value={form.patient_name} onChange={set('patient_name')} placeholder="Full name"
                  style={{ padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Phone *</label>
                <input value={form.phone} onChange={handlePhone} placeholder="03XX-XXXXXXX"
                  style={{ padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Doctor</label>
                <select value={form.doctor_id} onChange={set('doctor_id')}
                  style={{ padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none' }}>
                  <option value="">-- Select doctor (optional) --</option>
                  {doctors.map(d => <option key={d.id} value={d.id}>{d.name} — {d.specialty}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Date *</label>
                <input type="date" value={form.appointment_date} onChange={set('appointment_date')}
                  style={{ padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Time *</label>
                <input type="time" value={form.appointment_time} onChange={set('appointment_time')}
                  style={{ padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Notes</label>
                <input value={form.notes} onChange={set('notes')} placeholder="Optional notes"
                  style={{ padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button type="submit" disabled={saving}
                style={{ padding: '10px 28px', background: GREEN, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                {saving ? 'Booking...' : 'Book Appointment'}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ padding: '10px 20px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {loading
        ? <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading...</div>
        : <AppTable data={list} />
      }
    </div>
  );
}
